import {connect} from 'react-redux';

import {supportsPopout} from '../../../../libs/shared/core/src/helpers/environment/platformHelpers';
import {FrontClickEvent} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {MessageModel, MessageStatusesEnum} from '../../../../libs/shared/core/src/models/api/messageModel';
import {PopoutLocationsEnum} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {getHasAutoShareDrafts} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {CellsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {findConversationsReminder} from '../../../helpers/models/conversationHelpers';
import {MessageDateGetter, toDraftSummary} from '../../../helpers/models/messageHelpers';
import {AppState} from '../../../store/appState';
import {ThunkAction} from '../../../store/appThunk';
import {makeGetApplicationChannelTypeFromConversation} from '../../../store/data/applicationChannelTypes/applicationChannelTypesSelectors';
import {makeGetVisualRecipients} from '../../../store/data/contacts/recipientsSelectors';
import {getConversations} from '../../../store/data/dataSelectors';
import {makeGetExternalChannelTypeFromConversation} from '../../../store/data/externalChannelTypes/externalChannelTypesSelectors';
import {makeGetMessageNamespace} from '../../../store/data/messages/messagesSelectors';
import {getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {popoutComposerAction} from '../../../store/panels/composer/composerPanelActions';
import {InboxesTeammateMessagesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {popoutMessageViewerAction} from '../../../store/panels/message/messageViewerPanelActions';
import {popoutConversationViewerAction} from '../../../store/panels/viewer/viewerPanelActions';
import {MessageCell, MessageCellClickEventHandler} from './messageCell';

/*
 * Props.
 */

export interface MessageCellContainerProps {
  panelId: CellsPanelId;
  message: MessageModel;
  dateGetter: MessageDateGetter;
  cellsContext: CellsContext;
  onClick?: MessageCellClickEventHandler;
  parameters: InboxesTeammateMessagesParameters;
}

/*
 * Selectors.
 */

function getMessage(_state: AppState, props: MessageCellContainerProps) {
  return props.message;
}

function getDateGetter(_state: AppState, props: MessageCellContainerProps) {
  return props.dateGetter;
}

function getCellsContext(_state: AppState, props: MessageCellContainerProps) {
  return props.cellsContext;
}

function makeGetConversation() {
  return createSelector(
    [getMessage, getConversations],
    (message, conversations) => conversations[message.conversationId],
  );
}

function makeGetConversationSubject(getConversation: ReturnType<typeof makeGetConversation>) {
  return createSelector([getConversation], (conversation) => conversation && conversation.subject);
}

function makeGetDraftSummary(getConversation: ReturnType<typeof makeGetConversation>) {
  return createSelector(
    [getMessage, getConversation, getHasAutoShareDrafts, getUserId],
    (message, conversation, hasAutoShareDrafts, userId) =>
      toDraftSummary(message, conversation, hasAutoShareDrafts, userId),
  );
}

function makeGetDate() {
  return createSelector([getMessage, getDateGetter], (message, dateGetter) => dateGetter(message));
}

function makeGetReminderDate() {
  return createSelector(
    [getMessage, getConversations, getCellsContext],
    (message, conversations, cellsContext) => {
      const conversation = conversations[message.conversationId];
      if (!conversation) {
        return undefined;
      }

      const reminder = findConversationsReminder(cellsContext, [conversation]);
      return reminder && reminder.remindAt;
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MessageCellContainerProps>()(() => {
  const getConversation = makeGetConversation();
  const getNamespace = makeGetMessageNamespace(getMessage);
  const getDraftSummary = makeGetDraftSummary(getConversation);

  return {
    conversationSubject: makeGetConversationSubject(getConversation),
    draft: getDraftSummary,
    recipients: makeGetVisualRecipients(getNamespace, getMessage),
    date: makeGetDate(),
    reminderDate: makeGetReminderDate(),
    externalChannelType: makeGetExternalChannelTypeFromConversation(getConversation),
    applicationChannelType: makeGetApplicationChannelTypeFromConversation(getConversation),
    hasAutoShareDrafts: getHasAutoShareDrafts,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<MessageCellContainerProps>()((props) => ({
  onClick:
    (uid: string, event: FrontClickEvent): ThunkAction =>
    (dispatch, getState) => {
      const {panelId, message, onClick} = props;
      const conversation = makeGetConversation()(getState(), props);

      if (!conversation) {
        if (message.status === MessageStatusesEnum.DRAFT_COMPOSE) {
          dispatch(popoutComposerAction(panelId, message.uid));
        } else {
          dispatch(popoutMessageViewerAction(panelId, message.uid));
        }
      }

      if (onClick) {
        onClick(uid, event);
      }
    },
  onDoubleClick: (): ThunkAction => (dispatch, getState) => {
    if (!supportsPopout()) {
      return;
    }

    const {message} = props;
    dispatch(
      popoutConversationViewerAction(
        message.conversationId,
        undefined,
        undefined,
        undefined,
        PopoutLocationsEnum.DOUBLE_CLICK_P2,
      ),
    );
  },
}));

export const MessageCellContainer = connect(mapStateToProps, mapDispatchToProps)(MessageCell);
